import type { AdobeGateway } from './AdobeGateway';
import { type AdobeQualityOfExperience, MediaHeartbeatEvents } from './AdobeTrackingMachine';
import type {
  UnifiedTrackingMachineLoadEvent,
  UnifiedTrackingMachineTimeUpdateEvent,
} from '../tracking/UnifiedTrackingMachine';

import type { AdobeMetadata } from './types';

export class BasicAdobePlaybackHelper {
  private gateway: AdobeGateway;
  lastPlayhead: number;
  private isBuffering: boolean;
  private isSeeking: boolean;
  isPlaying: boolean;
  private lastQualityOfExperience: AdobeQualityOfExperience | undefined;
  isInSession: boolean;

  constructor(adobeGateway: AdobeGateway) {
    this.gateway = adobeGateway;
  }

  updatePlayhead(playhead: number) {
    if (this.lastPlayhead !== playhead) {
      this.gateway.updatePlayhead(playhead);
      this.lastPlayhead = playhead;

      if (this.isBuffering) {
        this.gateway.trackEvent(MediaHeartbeatEvents.bufferComplete);
        this.isBuffering = false;
      }

      if (this.isSeeking) {
        this.gateway.trackEvent(MediaHeartbeatEvents.seekComplete);
        this.isSeeking = false;
      }
      if (!this.isPlaying) {
        this.gateway.trackEvent(MediaHeartbeatEvents.play);
        this.isPlaying = true;
      }
    }
  }

  sendQualityOfExperience(value: UnifiedTrackingMachineTimeUpdateEvent['value']) {
    const isSame = (a: AdobeQualityOfExperience, b: AdobeQualityOfExperience) => {
      return (
        (a.droppedFrames ?? 0) === (b.droppedFrames ?? 0) &&
        (a.currentBitrate ?? 0) === (b.currentBitrate ?? 0)
      );
    };

    if (!this.lastQualityOfExperience || !isSame(this.lastQualityOfExperience, value)) {
      this.gateway.updateQoEObject({
        'media.qoe.bitrate': value.currentBitrate ?? 0,
        'media.qoe.droppedFrames': value.droppedFrames ?? 0,
        'media.qoe.framesPerSecond': 0,
        'media.qoe.timeToStart': 0,
      });
      this.lastQualityOfExperience = value;
    }
  }

  onSeeking() {
    if (this.isPlaying && this.lastPlayhead && !this.isSeeking && !this.isBuffering) {
      this.isSeeking = true;
      this.gateway.trackEvent(MediaHeartbeatEvents.seekStart);
    }
  }

  onDestroy(): void {
    if (this.isInSession) {
      this.gateway.trackSessionEnd();
      this.isInSession = false;
    }
  }

  onBuffering() {
    if (this.isInSession && !this.isBuffering) {
      this.gateway.trackEvent(MediaHeartbeatEvents.bufferStart);
      this.isBuffering = true;
    }
  }

  onPause() {
    if (this.isPlaying) {
      this.isPlaying = false;
      this.gateway.trackEvent(MediaHeartbeatEvents.pause);
    }
  }

  onStop(liveStop?: boolean) {
    if (this.isInSession && liveStop) {
      this.gateway.trackSessionEnd();
      this.isInSession = false;
    } else if (this.isInSession) {
      this.gateway.trackComplete();
      this.gateway.trackSessionEnd();
      this.isInSession = false;
    }
    this.isPlaying = false;
  }

  getBasicMetadata(event: UnifiedTrackingMachineLoadEvent) {
    const { media } = event.value;
    const { videoInfo, broadcast } = media.playback.tracking.adobe!;

    const trackSubtitleState = {
      'video.subtitles_available': (event.value.subtitlesAvailable ?? false).toString(),
      'video.subtitles_enabled': (event.value.subtitlesEnabled ?? false).toString(),
    };

    return {
      ...videoInfo,
      ...trackSubtitleState,
      'video.timeshiftAllowed': 'false',
      'media.channel': broadcast,
      isUserLoggedIn: `${Boolean((videoInfo as any)?.['segment.custId'])}`,
      'video.fallback': 'false',
    } as AdobeMetadata;
  }
}
